import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./CertificateModel.module.css";
import { getAuthToken } from "../../util/auth";
import { getApiChara } from "../../util/getApi";
import ReactQuill from "react-quill";
import html2pdf from "html2pdf.js";
import "react-quill/dist/quill.snow.css";
import CertificateLayout from "./CertificateLayout";
import { toast } from "react-toastify";
import AuthContext from "../../store/auth-context";
import { useTranslation } from "react-i18next";
import { getHeaderHtml } from "../../util/headerUtil";
import { themeColors } from "../../util/themeColors";

const CertificateModel = ({ PatientData, certificateData, OnCloseModalBlock }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const [selectedCert, setSelectedCert] = useState("");
  const [fullCertificateData, setFullCertificateData] = useState([]);
  const [editableDescription, setEditableDescription] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewMode, setPreviewMode] = useState(false);
  const [buttonsEnabled, setButtonsEnabled] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState("a4"); // 🔹 New: page format
  // const [printAfterSubmit, setPrintAfterSubmit] = useState(false);
  const [printAfterSubmit, setPrintAfterSubmit] = useState(
    authCtx.hospitalData.PrintCertificate === true || authCtx.hospitalData.PrintCertificate === "true"
  );
  const previewRef = useRef();
  useEffect(() => {
    if (fullCertificateData?.description) {
      setEditableDescription(fullCertificateData.description);
      setPreviewHtml(fullCertificateData.description);
      setButtonsEnabled(false);
    }
  }, [fullCertificateData]);

  const handleCertificateChange = async (event) => {
    setSelectedCert(event.target.value);
    setButtonsEnabled(false);
    const token = getAuthToken();
    if (token === null || token === "EXPIRED") {
      navigate("/");
    }
    const apiChar = getApiChara();
    const apiUrl = `${apiChar}/certificate/getCertificateByName?certificateName=${encodeURIComponent(
      event.target.value
    )}`;

    try {
      const response = await fetch(apiUrl, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });
      if (!response.ok) {
        throw new Error("Could not fetch certificate details");
      }
      const data = await response.json();
      setFullCertificateData(data);
    } catch (error) {
      console.error("Error fetching certificate data:", error);
      toast.error(t("Could not fetch certificate details!"));
    }
  };

  const generatePreview = () => {
    setPreviewHtml(editableDescription);
    setPreviewMode(true);
    setButtonsEnabled(true);
  };

  const handleDownload = () => {
    if (!previewRef.current) return;

    const patient = PatientData?.patientId || {};
    const patientName = `${patient?.firstName || ""}_${patient?.middleName || ""}_${patient?.lastName || ""}`
      .trim()
      .replace(/\s+/g, "_");

    const hospitalName = authCtx.hospitalData.hospitalName || "Hospital Name";
    const address = authCtx.hospitalData.hospitalAddress || "";
    const reg = authCtx.hospitalData.regnumber || "-";
    const isA5 = selectedFormat === 'a5';
    const padding = isA5 ? '12px 20px' : '20px 35px';
    const fontSizeMain = isA5 ? '16px' : '18px';
    const fontSizeHeader = isA5 ? '15px' : '17px';
    const lineHeight = isA5 ? '1.5' : '1.8';

    const element = document.createElement("div");
    const themeColor = themeColors[authCtx.hospitalData?.applicationThemeColour || 'TEAL']?.primary || '#006d77';

    element.innerHTML = `
      <div style="
        width: 100%;
        height: ${isA5 ? '208mm' : '295mm'};
        padding: 0;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
        position: relative;
        background: white;
      ">
        
        <!-- Standardized Header -->
        <div style="width: 100%; padding: 0 10px; box-sizing: border-box;">
          ${getHeaderHtml(authCtx.hospitalData, true)}
        </div>

        <div style="margin: 2mm 4mm; padding: ${padding}; border: 3px solid ${themeColor}; border-radius: 8px; flex-grow: 1; display: flex; flex-direction: column; background: #fff; min-height: 0;">
          <!-- Structured Patient/Date Info -->
          <div style="display: flex; justify-content: space-between; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 1.5px solid #eee; font-family: 'Times New Roman', Times, serif; font-style: italic; color: #111; font-weight: 600; font-size: ${fontSizeHeader};">
            <div style="text-align: left; width: 60%;">
              <div style="margin-bottom: 5px;">${t("Name")} : ${patient?.firstName || ""} ${patient?.middleName || ""} ${patient?.lastName || ""}</div>
              <div>${t("Address")} : ${patient?.address || "-"}</div>
            </div>
            <div style="text-align: right; width: 40%;">
              <div style="margin-bottom: 5px;">${t("Mobile")} : ${patient?.mobile || "-"}</div>
              <div>${t("Date")} : ${new Date().toLocaleDateString('en-GB')}</div>
            </div>
          </div>

          <!-- Main Body Content -->
          <div style="flex-grow: 1; font-size: ${fontSizeMain}; line-height: ${lineHeight}; color: #111; text-align: justify; padding: 0; font-family: 'Times New Roman', Times, serif; font-style: italic; font-weight: 500; letter-spacing: 0.1px;">
             ${previewHtml}
          </div>

          <!-- Bottom Signature Section -->
          <div style="display: flex; justify-content: flex-end; width: 100%; margin-top: ${isA5 ? '10px' : '25px'};">
              <div style="text-align: center; min-width: ${isA5 ? '160px' : '200px'};">
                  <div style="border-top: 1.5px solid #000; margin-bottom: 5px; width: 100%;"></div>
                  <div style="font-weight: bold; font-size: ${isA5 ? '12px' : '13px'}; color: #000; text-transform: uppercase; font-family: sans-serif;">${t("Authorized Signatory")}</div>
                  <div style="font-size: ${isA5 ? '10px' : '11px'}; color: #555; margin-top: 2px; font-family: sans-serif;">${hospitalName}</div>
              </div>
          </div>
        </div>
      </div>
    `;

    const opt = {
      margin: 0,
      filename: `${patientName || "certificate"}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true },
      jsPDF: { unit: "mm", format: selectedFormat, orientation: "portrait" },
    };

    html2pdf().set(opt).from(element).save();
  };


  const handlePrint = (content = previewHtml) => {
    // Removed DOM ref check since content is from state

    const patient = PatientData?.patientId || {};
    const hospitalName = authCtx.hospitalData.hospitalName || "";
    const printWindow = window.open("", "_blank", "width=900,height=650");
    if (!printWindow) return;

    const themeColor = themeColors[authCtx.hospitalData?.applicationThemeColour || 'TEAL']?.primary || '#006d77';

    printWindow.document.write(`
      <html>
        <head>
          <title>${selectedCert} Certificate</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; padding: 0; color: #333; }
            .cert-outer { height: 100%; display: flex; flex-direction: column; background: white; }
            .cert-border { margin: 5px 10px; padding: 20px 35px; border: 4px solid ${themeColor}; border-radius: 8px; flex-grow: 1; display: flex; flex-direction: column; }
            .header-info { display: flex; justify-content: space-between; margin-bottom: 20px; font-family: 'Times New Roman', Times, serif; font-style: italic; font-size: 19px; border-bottom: 2.5px solid #eee; padding-bottom: 15px; line-height: 1.4; color: #111; font-weight: 600; }
            .info-left { text-align: left; }
            .info-right { text-align: right; }
            .content-area { font-family: 'Times New Roman', Times, serif; font-style: italic; font-size: 19px; line-height: 1.8; text-align: justify; flex-grow: 1; padding: 0; color: #111; font-weight: 500; }
            .footer-sig { margin-top: 35px; display: flex; justify-content: flex-end; }
            .sig-box { text-align: center; border-top: 2px solid #000; width: 220px; padding-top: 8px; }
            .sig-text { font-weight: bold; font-size: 14px; text-transform: uppercase; color: #000; font-family: 'Segoe UI', Arial, sans-serif; }
            .hosp-name { font-size: 12px; color: #555; font-family: 'Segoe UI', Arial, sans-serif; }
            @page {
              size: ${selectedFormat};
              margin: 0;
            }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; margin: 0; padding: 0; }
              .cert-outer { 
                 width: 100%;
                 height: 100vh;
                 display: flex; 
                 flex-direction: column; 
                 page-break-after: always;
              }
              .cert-border { 
                 flex-grow: 1; 
                 border: 4px solid ${themeColor} !important; 
                 margin: 2mm 4mm; 
                 padding: ${selectedFormat === 'a5' ? '12px 20px' : '20px 35px'} !important;
                 min-height: 0 !important; 
              }
              .header-info { 
                 font-size: ${selectedFormat === 'a5' ? '16px' : '19px'} !important; 
                 margin-bottom: 10px !important; 
                 padding-bottom: 8px !important; 
              }
              .content-area { 
                 font-size: ${selectedFormat === 'a5' ? '16px' : '19px'} !important; 
                 line-height: 1.6 !important;
              }
              .footer-sig { margin-top: 15px !important; }
              .sig-box { width: 180px !important; }
            }
          </style>
        </head>
        <body>
          <div class="cert-outer">
            <div style="width: 100%; padding: 0 10px; box-sizing: border-box;">
              ${getHeaderHtml(authCtx.hospitalData)}
            </div>

            <div class="cert-border">
              <div class="header-info">
                <div class="info-left">
                  <div><strong>${t("Name")}:</strong> ${patient?.firstName || ""} ${patient?.middleName || ""} ${patient?.lastName || ""}</div>
                  <div><strong>${t("Address")}:</strong> ${patient?.address || "-"}</div>
                </div>
                <div class="info-right">
                  <div><strong>${t("Mobile")}:</strong> ${patient?.mobile || "-"}</div>
                  <div><strong>${t("Date")}:</strong> ${new Date().toLocaleDateString('en-GB')}</div>
                </div>
              </div>

              <div class="content-area">
                ${content}
              </div>

              <div class="footer-sig">
                <div class="sig-box">
                  <div class="sig-text">${t("Authorized Signatory")}</div>
                  <div class="hosp-name">${hospitalName}</div>
                </div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  };

  // 🔹 Save certificate to DB
  const handleSaveToDB = async () => {
    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      navigate("/");
      return;
    }
    const patient = PatientData?.patientId || {};
    const apiChar = getApiChara();
    const apiUrl = `${apiChar}/PatientCertificate/createPatientCertificate`;
    const userId = authCtx.userId;
    const userName = authCtx.userName;
    const payload = {
      patientId: patient?.patientId,
      certificateName: fullCertificateData?.cerName || " ",
      certificateId: fullCertificateData?.cerId || " ",
      patientName: `${patient?.firstName || ""} ${patient?.middleName || ""} ${patient?.lastName || ""}`.trim(),
      // issuedDate: new Date().toISOString(),
      issuedById: userId,
      description: editableDescription,
      issuedByName: userName,
    };

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to save certificate");
      }

      toast.success("Certificate saved successfully!");

      // 🔹 Print if checkbox is checked
      if (printAfterSubmit) {
        handlePrint(editableDescription);
      }

      // Close modal after saving
      OnCloseModalBlock();

    } catch (error) {
      console.error("Error saving certificate:", error);
      toast.error("Error saving certificate!");
    }
  };

  return (
    <div>
      <div className={styles.backdrop} onClick={OnCloseModalBlock} />
      <div className={styles.modal}>
        <header className={styles.header}>
          <div className="row">
            <div className="col-12" style={{ position: 'relative' }}>
              <h3 className={styles.heading} style={{ textAlign: 'center', margin: '0 40px' }}>{t("Get Your Certificate")}</h3>
              <button
                className={styles.closeButton}
                onClick={OnCloseModalBlock}
                style={{ position: 'absolute', right: '15px', top: '50%', transform: 'translateY(-50%)' }}
              >
                <i className="fa fa-close"></i>
              </button>
            </div>
          </div>
        </header>

        <div className={styles.content}>
          <div className="container-fluid">
            {/* Top Bar: Name & Selection */}
            <div className={styles.topBar}>
              <div className={styles.patientGroup}>
                <div className={styles.iconWrapper}>
                  <i className="fa fa-user" style={{ color: 'var(--primary-color)' }}></i>
                </div>
                <div>
                  <label className={styles.label}>{t("Patient Name")}</label>
                  <div className={styles.value}>
                    {PatientData
                      ? `${PatientData?.patientId?.firstName || ""} ${PatientData?.patientId?.middleName || ""} ${PatientData?.patientId?.lastName || ""}`
                        .trim()
                        .replace(/\s+/g, " ")
                        .toUpperCase()
                      : ""}
                  </div>
                </div>
              </div>

              <div className={styles.selectGroup}>
                <div className={styles.iconWrapper}>
                  <i className="fa fa-file-text" style={{ color: 'var(--primary-color)' }}></i>
                </div>
                <div style={{ flex: 1 }}>
                  <label htmlFor="certificateSelect" className={styles.label}>
                    {t("Certificate Type")}
                  </label>
                  <select
                    id="certificateSelect"
                    className={styles.certificateDropdown}
                    value={selectedCert}
                    onChange={handleCertificateChange}
                  >
                    <option value="" disabled>{t("Select Certificate")}</option>
                    {certificateData.map((cert, index) => (
                      <option key={index} value={cert}>{cert}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Editor & Actions */}
            {fullCertificateData?.description && (
              <div className={styles.richEditor}>
                <ReactQuill
                  value={editableDescription}
                  onChange={setEditableDescription}
                  theme="snow"
                  style={{ height: '200px', marginBottom: '40px', overflowY: 'auto' }}
                />

                <div className={styles.btnconatiner}>
                  {/* Print Checkbox */}
                  <label className={styles.printCheckboxLabel}>
                    <input
                      type="checkbox"
                      checked={printAfterSubmit}
                      onChange={(e) => {
                        setPrintAfterSubmit(e.target.checked);
                      }}
                      className={styles.checkboxInput}
                    />
                    {t("Print")}
                  </label>

                  <div className={styles.formatGroup}>
                    <label className={styles.label}>{t("Format:")}</label>
                    <select
                      className={styles.certificateDropdown}
                      value={selectedFormat}
                      onChange={(e) => setSelectedFormat(e.target.value)}
                      style={{ width: '70px', padding: '4px 8px', fontSize: '12px' }}
                    >
                      <option value="a4">A4</option>
                      <option value="a5">A5</option>
                    </select>
                  </div>

                  <button
                    className={styles.button}
                    onClick={generatePreview}
                    style={{ backgroundColor: '#6c757d' }} // Secondary color for preview
                  >
                    <i className="fa fa-eye" style={{ marginRight: '5px' }}></i> {t("Preview & Print")}
                  </button>

                  <button
                    className={styles.button}
                    onClick={handleSaveToDB}
                  // disabled={!buttonsEnabled}
                  >
                    <i className="fa fa-save" style={{ marginRight: '5px' }}></i> {t("Save Certificate")}
                  </button>
                </div>
              </div>
            )}

            {/* Live Preview Section */}
            {/* Preview Modal Overlay (Popup) */}
            {previewHtml && previewMode && (
              <div className={styles.previewBackdrop}>
                <div className={styles.previewModal}>
                  <div className={styles.previewHeader}>
                    <div style={{ flex: 1 }}></div>
                    <h4 style={{ flex: 1, textAlign: 'center', margin: 0 }}>{t("Certificate Preview")}</h4>
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        className={styles.closePreviewBtn}
                        onClick={() => setPreviewMode(false)}
                        title={t("Close Preview")}
                      >
                        <i className="fa fa-times"></i>
                      </button>
                    </div>
                  </div>

                  <div className={styles.previewBody}>
                    <div ref={previewRef} className={styles.previewPaper} style={{
                      width: selectedFormat === 'a5' ? '148mm' : '210mm',
                      minHeight: selectedFormat === 'a5' ? '210mm' : '297mm',
                      margin: '0 auto',
                      transform: selectedFormat === 'a5' ? 'scale(0.85)' : 'none',
                      transformOrigin: 'top center'
                    }}>
                      <CertificateLayout
                        patient={PatientData?.patientId}
                        content={<div dangerouslySetInnerHTML={{ __html: previewHtml }} />}
                        format={selectedFormat}
                      />
                    </div>
                  </div>

                  <div className={styles.previewFooter}>
                    <button
                      className="btn-premium px-4 py-1"
                      onClick={() => handlePrint()}
                      title={t("Print")}
                      style={{ fontSize: '12px' }}
                    >
                      <i className="fa fa-print me-1"></i> {t("PRINT")}
                    </button>

                    <button
                      className="btn-export-premium px-4 py-1 ms-3"
                      onClick={handleDownload}
                      title={t("Download PDF")}
                      style={{ fontSize: '12px' }}
                    >
                      <i className="fa fa-file-pdf-o me-1"></i> {t("EXPORT PDF")}
                    </button>

                    <button
                      className="btn-premium px-4 py-1 ms-3"
                      onClick={() => setPreviewMode(false)}
                      style={{
                        fontSize: '12px',
                        height: '34px',
                        backgroundColor: '#f2f2f2',
                        borderColor: '#e0e0e0',
                        color: '#666',
                        boxShadow: 'none',
                        transition: 'all 0.2s'
                      }}
                    >
                      <i className="fa fa-close me-1"></i> {t("CLOSE")}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateModel;
